import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/reducer';
import TagmeButton from '../TagmeButton';
import { COLORS } from '../../common/constants';
import { FooterStyles, OtpConfirmationWrapper } from './styles';
import { userManagerApi } from '../../api/axios.config';
import { TrackEventsType, useCurrentUser } from '../../Services/current-user.service';
import { ModalProps, ModalTypes } from '../TagmeModal/interface';
import TagmeModal from '../TagmeModal';
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import { formatPhoneNumber } from '../../utils/phone.utils';

interface OtpConfirmationProps {
	form: any;
	onReturnClick: () => any
	enterWaitlist: () => any
}

function OtpConfirmation({
	form,
	onReturnClick,
	enterWaitlist
}: OtpConfirmationProps) {
	const { t, i18n } = useTranslation();
	const venueInfo = useSelector((state: RootState) => state.entities.venueInfo);

	const [modalInfo, setModalInfo] = useState<ModalProps>({
		show: false,
		type: ModalTypes.error,
	})
	const closeModal = useCallback(() => {
		setModalInfo(previousModalInfoState => ({ ...previousModalInfoState, show: false }))
	}, [])

	const { createUser, sendOtp, registerEvent } = useCurrentUser();


	const [otpValues, setOtpValues] = useState<string[]>(Array(6).fill(''));
	const [remaining, setRemaining] = useState<number>(60);
	const [canResendOtp, setCanResendOtp] = useState<boolean>(false);
	const [wasToggled, setWasToggled] = useState<boolean>(false);
	const [isValidatingOtp, setIsValidatingOtp] = useState<boolean>(false);
	const [useEmail, setUseEmail] = useState(false);
	const [isOtpValid, setIsOtpValid] = useState<boolean>(false);

	const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

	const email = form.customer?.email || ""
	const phone = form.customer?.phone ? `+${form.customer.phone.replace(/\D/g, '')}` : ""

	useEffect(() => {
		otpRefs.current[0]?.focus();
		sendOtpToUser();
	}, [useEmail]);

	useEffect(() => {
		const interval = setInterval(() => {
			setRemaining(prev => {
				if (prev <= 1) {
					clearInterval(interval)
					setCanResendOtp(true)
					return 0;
				}
				return prev - 1;
			});
		}, 500);

		return () => clearInterval(interval)
	}, [useEmail]);


	const formatTime = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
		let value = (e.target.value || '').replace(/\D/g, '')

		if (value.length > 1) {
			const digits = value.slice(0, otpRefs.current.length).split('')
			setOtpValues(digits)
			otpRefs.current[otpRefs.current.length - 1]?.focus()
			checkAndSubmitOtp(digits)
			return
		}

		value = value.slice(-1)

		const updated = [...otpValues];
		updated[index] = value;
		setOtpValues(updated);
		if (!value) return;
		if (index < otpRefs.current.length - 1) {
			otpRefs.current[index + 1]?.focus();
		} else {
			checkAndSubmitOtp(updated);
		}
	};

	const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
		e.target.select()
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
		if (e.key === 'Backspace') {
			e.preventDefault();

			const updated = [...otpValues];

			if (updated[index]) {
				// Limpa o campo atual
				updated[index] = '';
				setOtpValues(updated);
			} else if (index > 0) {
				// Vai pro anterior se já estiver vazio
				otpRefs.current[index - 1]?.focus();

				updated[index - 1] = '';
				setOtpValues(updated);
			}
		}
	};

	const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
		e.preventDefault();
		const pasted = e.clipboardData?.getData('text') || '';
		const digits = pasted.replace(/\D/g, '').slice(0, otpRefs.current.length);
		if (digits.length !== otpRefs.current.length) return;
		const arr = digits.split('');
		setOtpValues(arr);
		otpRefs.current[otpRefs.current.length - 1]?.focus();
		checkAndSubmitOtp(arr);
	};

	function splitName(name?: string): { firstName: string; lastName: string } | null {
		const norm = (name ?? '')
			.replace(/[\u00A0\u2007\u202F]/g, ' ')
			.replace(/[\u200B-\u200D\uFEFF]/g, '')
			.trim()
			.replace(/\s+/g, ' ')

		if (!norm) return null

		const parts = norm.split(' ');
		if (parts.length === 1)
			return null

		return {
			firstName: parts.slice(0, -1).join(' '),
			lastName: parts[parts.length - 1],
		};
	}

	const checkAndSubmitOtp = async (otp: string[]) => {
		const full = otp.join('')
		if (full.length === 6) {
			setIsValidatingOtp(true)
			try {
				const nameParts = splitName(form?.customer?.name || "")
				if (!nameParts) {
					setModalInfo({
						show: true,
						onClose: closeModal,
						type: ModalTypes.error,
						message: 'Por favor, informe seu nome completo.',
						buttons: [
							{
								label: 'OK',
								action: closeModal,
							},
						],
					})
					return
				}
				const { firstName, lastName } = nameParts;

				await createUser({
					firstName,
					lastName,
					phone,
					email,
					productName: 'Tagme',
					otp: full
				});

				// Dont need to wait
				if (venueInfo?._id) {
					registerEvent(venueInfo?._id, TrackEventsType.WAITLIST_LOGIN, {
						firstName,
						lastName,
						phone,
						email,
						productName: 'Tagme'
					})
				}

				setIsOtpValid(true);
			} catch (err: any) {
				const errorMessage =
					err?.response?.data?.message ||
					err?.response?.data?.error ||
					err?.message ||
					'Ocorreu um erro inesperado.';

				setModalInfo({
					show: true,
					onClose: closeModal,
					type: ModalTypes.error,
					message: errorMessage,
					buttons: [
						{
							label: 'OK',
							action: closeModal,
						},
					],
				})
			} finally {
				setIsValidatingOtp(false)
			}
		}
	};

	const sendOtpToUser = async () => {
		const target = useEmail ? email : phone
		const channel = useEmail ? 'email' : 'sms'
		setTimeout(() => {
			setModalInfo({
				show: true,
				onClose: closeModal,
				type: ModalTypes.success,
				message: useEmail ? t('reservation.codeSentEmail', { target: email }) : t('reservation.codeSentSms', { target: phone }),
				buttons: [

				],
			})
		}, 1000)
		try {
			await sendOtp(channel, target, venueInfo?._id || "", i18n.language);
		} catch (err) {
		}
	};

	const toggleOtpMethod = () => {
		setWasToggled(true)
		setUseEmail(prev => !prev)
		setOtpValues(Array(6).fill(''))
		otpRefs.current[0]?.focus()
	};

	return (
		<div className="container content">
			<OtpConfirmationWrapper
				colorMain={venueInfo.color.main || COLORS.main}
				colorBorder={venueInfo.color.border || COLORS.border}
				colorBackground={venueInfo.color.background || COLORS.background}
			>
				<div className="btn-back" onClick={onReturnClick}>
					<span><i className="tagmeicon tagmeicon-voltar"></i> {t('back')}</span>
				</div>

				<h4>{useEmail ? t('reservation.confirmEmail') : t('reservation.confirmPhone')}</h4>
				<p>{t('reservation.toValidate')} <strong>{useEmail ? email : formatPhoneNumber(phone)}</strong></p>

				<div className="otp-wrapper">
					<div className="otp-input">
						<div className={`group`}>
							{[0, 1, 2].map(i => (
								<input
									key={i}
									autoComplete={i === 0 ? 'one-time-code' : 'off'}
									name={i === 0 ? 'one-time-code' : undefined}
									autoCorrect="off"
									autoCapitalize="off"
									inputMode="numeric"
									ref={el => otpRefs.current[i] = el}
									type="text"
									className={`otp-box`}
									value={otpValues[i]}
									disabled={isValidatingOtp || isOtpValid}
									onChange={(e) => handleChange(e, i)}
									onKeyDown={(e) => handleKeyDown(e, i)}
									onPaste={handlePaste}
									onFocus={handleFocus}
								/>
							))}
						</div>
						<span className="dash-line"></span>
						<div className="group">
							{[3, 4, 5].map(i => (
								<input
									key={i}
									inputMode="numeric"
									ref={el => otpRefs.current[i] = el}
									type="text"
									className={`otp-box`}
									value={otpValues[i]}
									disabled={isValidatingOtp || isOtpValid}
									onChange={(e) => handleChange(e, i)}
									onKeyDown={(e) => handleKeyDown(e, i)}
									onPaste={handlePaste}
									onFocus={handleFocus}
								/>
							))}
						</div>
					</div>

					{
						!isOtpValid && <div className="otp-timer">{formatTime(remaining)}</div>
					}

					<div className="otp-actions">
						<span>{t('reservation.notReceived')}?</span>{' '}
						<a
							className={`clickable ${!canResendOtp || isOtpValid ? 'disabled' : ''}`}
							onClick={() => canResendOtp && sendOtpToUser()}
						>
							{t('reservation.resend')}
						</a>
					</div>
				</div>

				<div className="centered">
					<button
						className="btn toggle-method"
						onClick={toggleOtpMethod}
						disabled={wasToggled && !canResendOtp || isOtpValid}
					>
						{useEmail ? t('reservation.getCodeByPhone') : t('reservation.getCodeByEmail')}
					</button>
				</div>
			</OtpConfirmationWrapper>

			<FooterStyles
				colorMain={venueInfo.color.main || COLORS.main}
				colorBorder={venueInfo.color.border || COLORS.border}
				colorBackground={venueInfo.color.background || COLORS.background}
			>
				<TagmeButton
					type="button"
					label={t('Get in waitlist')}
					colorMain={venueInfo.color.main}
					disabled={!isOtpValid}
					handleClick={enterWaitlist}
				/>
			</FooterStyles>

			<TagmeModal {...modalInfo} />
		</div>
	);
}

export default OtpConfirmation;
